import Phaser from 'phaser';
import { ANIMATION, COIN, COIN_MAGNET_DELAY_MS } from '@/config/balance.config';
import { Haptics } from '@/game/fx/Haptics';
import type { Player } from './Player';

/**
 * Pojedyncza moneta dropowana przez zniszczony destructible.
 *
 * Cykl życia:
 *   1. Spawn w pozycji obiektu, scatter w losowym kierunku (krótki tween).
 *   2. Po COIN_MAGNET_DELAY_MS startuje magnet → chase gracza.
 *   3. Gdy w pickupDistance — pop scale → `onCollect(value)` → znika.
 *
 * Spin: scaleX oscyluje (1.0 → -0.4 → 1.0) co tworzy fake-3D rotation w 2D.
 */
export class Coin {
  readonly sprite: Phaser.GameObjects.Arc;
  private collected = false;
  private spinPhase: number = Math.random() * Math.PI * 2;
  private isMagneting = false;

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    readonly value: number,
    private readonly player: Player,
    private readonly onCollect: (value: number) => void,
    /**
     * Mnożnik z upgrade'u `magnet_range` (`magnetMult(level)`).
     * Skaluje (a) szybkość pogoni za graczem przez `lerpFactor`,
     * (b) `pickupDistance` przez `sqrt(mult)` — żeby snap rósł wolniej niż speed.
     * Default 1.0 dla bezpieczeństwa / testów.
     */
    private readonly magnetMult: number = 1,
  ) {
    this.sprite = scene.add.circle(x, y, 8, 0xf1c40f);
    this.sprite.setStrokeStyle(2, 0xd4a418);
    this.sprite.setDepth(10);

    // Scatter — krótki tween w losowy kierunek
    const angle = Math.random() * Math.PI * 2;
    const dist = Phaser.Math.Between(8, COIN.scatterRadius);
    scene.tweens.add({
      targets: this.sprite,
      x: x + Math.cos(angle) * dist,
      y: y + Math.sin(angle) * dist,
      duration: 220,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        // Magnet delay z jitter'em — nie wszystkie coiny startują magnetowanie w tej samej klatce.
        // Daje "waterfall" effect: monety lecą do gracza w falach, nie hurtowo.
        const jitter = Math.random() * COIN.magnetDelayJitterMs;
        scene.time.delayedCall(COIN_MAGNET_DELAY_MS + jitter, () => { this.isMagneting = true; });
      },
    });
  }

  /**
   * Per-frame tick — wywoływany przez WorldScene.update().
   * Spin (zawsze) + magnet chase (gdy aktywny) + pickup detection.
   */
  tick(time: number, dt: number): void {
    if (this.collected) return;

    // Spin — scaleX oscyluje
    const cfg = ANIMATION.coin;
    const t = time / 1000 + this.spinPhase;
    const phase = Math.sin(t * Math.PI * 2 * cfg.spinFrequencyHz);
    this.sprite.scaleX = 1 - cfg.spinAmplitude * (1 - Math.abs(phase));   // 1.0 ↔ 0.4

    // Magnet chase — żywy track gracza (lerp w stronę gracza)
    if (this.isMagneting) {
      const dx = this.player.x - this.sprite.x;
      const dy = this.player.y - this.sprite.y;
      const dist = Math.hypot(dx, dy);

      // Snap radius rośnie z magnet_range, ale wolniej niż speed (sqrt) —
      // żeby coiny nie "teleportowały się" przy wysokich poziomach upgrade'u.
      const snap = COIN.pickupDistance * Math.sqrt(this.magnetMult);
      if (dist < snap) {
        this.collect();
        return;
      }

      // Tempo dochodzenia liczone z magnetTravelMs (z initial distance)
      // Praktycznie: lerp 0.18 per frame przy 60 FPS daje ~400 ms transit z ~100 px.
      // magnetMult skaluje szybkość pogoni — gracz czuje "magnes" silniejszy.
      const lerpFactor = Math.min(1, dt * (1000 / COIN.magnetTravelMs) * 6 * this.magnetMult);
      this.sprite.x += dx * lerpFactor;
      this.sprite.y += dy * lerpFactor;
    }
  }

  private collect(): void {
    if (this.collected) return;
    this.collected = true;

    const cfg = ANIMATION.coin;
    // Pop scale przed zniknięciem
    this.scene.tweens.add({
      targets: this.sprite,
      scaleX: cfg.pickupBurstScale,
      scaleY: cfg.pickupBurstScale,
      alpha: 0,
      duration: cfg.pickupBurstMs,
      ease: 'Cubic.easeOut',
      onComplete: () => this.sprite.destroy(),
    });

    Haptics.coinPickup();
    this.onCollect(this.value);
  }
}
